/**
 * Proxy — a stand-in soldier body.
 *
 * The CHARACTERS team publishes `ctx.characters.spawn(opts)` with
 * `setLocomotion / setAimTarget / setLookTarget / play / hit / die / getBone /
 * weaponAttach / dispose`. Until that lands the AI must still look like men in
 * a firefight, so this implements the same interface over a pooled set of
 * `InstancedMesh` parts: seven draw calls for the whole enemy team, whatever
 * the head count. The moment `ctx.characters` appears, `AI.js` spawns through
 * it instead and this file is simply never used.
 *
 * owner: ai
 */
import * as THREE from 'three';

const PART_TORSO = 0, PART_HEAD = 1, PART_ARM_L = 2, PART_ARM_R = 3;
const PART_LEG_L = 4, PART_LEG_R = 5, PART_RIFLE = 6;
const PART_COUNT = 7;

const _m = new THREE.Matrix4();
const _m2 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _v = new THREE.Vector3();
const _one = new THREE.Vector3(1, 1, 1);
const _hidden = new THREE.Matrix4().makeScale(0, 0, 0);

/* ------------------------------------------------------------------ *
 * geometry — everything is authored with its pivot at the joint
 * ------------------------------------------------------------------ */
function buildGeometries() {
  const g = new Array(PART_COUNT);

  // torso: hips -> shoulders, plus a plate carrier slab so the silhouette
  // reads as a soldier and not a mannequin
  {
    const body = new THREE.BoxGeometry(0.40, 0.60, 0.24);
    body.translate(0, 0.30, 0);
    const vest = new THREE.BoxGeometry(0.44, 0.36, 0.30);
    vest.translate(0, 0.36, 0.005);
    const pack = new THREE.BoxGeometry(0.30, 0.26, 0.14);
    pack.translate(0, 0.34, -0.19);
    g[PART_TORSO] = mergeGeoms([body, vest, pack]);
  }
  {
    const head = new THREE.BoxGeometry(0.165, 0.20, 0.19);
    head.translate(0, 0.10, 0.008);
    const helmet = new THREE.SphereGeometry(0.115, 10, 7, 0, Math.PI * 2, 0, Math.PI * 0.62);
    helmet.translate(0, 0.115, 0);
    g[PART_HEAD] = mergeGeoms([head, helmet]);
  }
  {
    const arm = new THREE.CapsuleGeometry(0.052, 0.44, 3, 8);
    arm.translate(0, -0.27, 0);
    g[PART_ARM_L] = arm;
    g[PART_ARM_R] = arm.clone();
  }
  {
    const leg = new THREE.CapsuleGeometry(0.082, 0.66, 3, 8);
    leg.translate(0, -0.41, 0);
    g[PART_LEG_L] = leg;
    g[PART_LEG_R] = leg.clone();
  }
  {
    const body = new THREE.BoxGeometry(0.055, 0.075, 0.70);
    body.translate(0, 0, 0.10);
    const barrel = new THREE.BoxGeometry(0.028, 0.028, 0.30);
    barrel.translate(0, 0.012, 0.58);
    const mag = new THREE.BoxGeometry(0.036, 0.20, 0.075);
    mag.translate(0, -0.12, 0.02);
    const stock = new THREE.BoxGeometry(0.05, 0.10, 0.24);
    stock.translate(0, -0.012, -0.30);
    g[PART_RIFLE] = mergeGeoms([body, barrel, mag, stock]);
  }
  return g;
}

function mergeGeoms(list) {
  // Local merge so we never depend on BufferGeometryUtils' interleaving rules.
  let vcount = 0, icount = 0;
  for (const g of list) {
    vcount += g.attributes.position.count;
    icount += g.index ? g.index.count : g.attributes.position.count;
  }
  const pos = new Float32Array(vcount * 3);
  const nrm = new Float32Array(vcount * 3);
  const idx = new Uint16Array(icount);
  let vo = 0, io = 0;
  for (const g of list) {
    const p = g.attributes.position, n = g.attributes.normal;
    for (let i = 0; i < p.count; i++) {
      pos[(vo + i) * 3] = p.getX(i); pos[(vo + i) * 3 + 1] = p.getY(i); pos[(vo + i) * 3 + 2] = p.getZ(i);
      nrm[(vo + i) * 3] = n.getX(i); nrm[(vo + i) * 3 + 1] = n.getY(i); nrm[(vo + i) * 3 + 2] = n.getZ(i);
    }
    if (g.index) {
      for (let i = 0; i < g.index.count; i++) idx[io + i] = g.index.getX(i) + vo;
      io += g.index.count;
    } else {
      for (let i = 0; i < p.count; i++) idx[io + i] = i + vo;
      io += p.count;
    }
    vo += p.count;
    g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  out.setIndex(new THREE.BufferAttribute(idx, 1));
  out.computeBoundingSphere();
  return out;
}

/**
 * Plain standard materials on purpose.
 *
 * The materials library's entries carry triplanar / POM shader injections that
 * are authored for static world geometry; instancing them on a moving body is
 * an unnecessary risk for a placeholder. The real rig arrives with the
 * CHARACTERS team's own materials, so these only ever have to read as "soldier"
 * against sand-coloured concrete in haze: dark olive kit, matte, no shine.
 */
function partMaterial(ctx, part) {
  void ctx;
  switch (part) {
    case PART_HEAD:
      return new THREE.MeshStandardMaterial({
        color: new THREE.Color(0.115, 0.100, 0.080), roughness: 0.88, metalness: 0.0,
      });
    case PART_RIFLE:
      return new THREE.MeshStandardMaterial({
        color: new THREE.Color(0.020, 0.021, 0.024), roughness: 0.45, metalness: 0.75,
      });
    default:
      return new THREE.MeshStandardMaterial({
        color: new THREE.Color(0.058, 0.068, 0.040), roughness: 0.93, metalness: 0.0,
      });
  }
}

/* ------------------------------------------------------------------ */

export class ProxyPool {
  constructor(ctx, capacity = 32) {
    this.ctx = ctx;
    this.capacity = capacity;
    this.geoms = buildGeometries();
    this.meshes = new Array(PART_COUNT);
    this.root = new THREE.Group();
    this.root.name = 'ai-proxy-characters';
    for (let p = 0; p < PART_COUNT; p++) {
      const im = new THREE.InstancedMesh(this.geoms[p], partMaterial(ctx, p), capacity);
      im.name = `ai-proxy-part-${p}`;
      im.frustumCulled = false;
      im.castShadow = true;
      im.receiveShadow = true;
      im.userData.noCollide = true;
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      for (let i = 0; i < capacity; i++) im.setMatrixAt(i, _hidden);
      im.instanceMatrix.needsUpdate = true;
      im.count = capacity;
      this.meshes[p] = im;
      this.root.add(im);
    }
    ctx.scene.add(this.root);
    this.free = [];
    for (let i = capacity - 1; i >= 0; i--) this.free.push(i);
    this.live = [];
  }

  spawn(opts = {}) {
    const slot = this.free.pop();
    if (slot === undefined) return null;
    const c = new ProxyCharacter(this, slot, opts);
    this.live.push(c);
    return c;
  }

  release(c) {
    const i = this.live.indexOf(c);
    if (i >= 0) this.live.splice(i, 1);
    for (let p = 0; p < PART_COUNT; p++) {
      this.meshes[p].setMatrixAt(c.slot, _hidden);
      this.meshes[p].instanceMatrix.needsUpdate = true;
    }
    this.free.push(c.slot);
  }

  /** Cosmetic pose update; safe to run at frame rate. */
  update(dt) {
    for (const c of this.live) c._pose(dt);
    for (let p = 0; p < PART_COUNT; p++) this.meshes[p].instanceMatrix.needsUpdate = true;
  }

  setVisible(on) { this.root.visible = !!on; }

  dispose() {
    for (let p = 0; p < PART_COUNT; p++) {
      this.meshes[p].geometry.dispose();
      this.meshes[p].material.dispose();
    }
    this.root.removeFromParent();
  }
}

/* ------------------------------------------------------------------ */

class ProxyCharacter {
  constructor(pool, slot, opts) {
    this.pool = pool;
    this.slot = slot;
    this.isProxy = true;

    this.position = new THREE.Vector3().copy(opts.position || _v.set(0, 0, 0));
    this.yaw = opts.yaw || 0;
    this.stance = 1;              // 1 = stand, 0 = crouch
    this.stanceTarget = 1;
    this.speed = 0;
    this.strafe = 0;
    this.phase = 0;
    this.aim = new THREE.Vector3(0, 1.5, 1);
    this.look = new THREE.Vector3(0, 1.5, 1);
    this.aimYaw = 0; this.aimPitch = 0;
    this.headYaw = 0; this.headPitch = 0;
    this.flinch = 0;
    this.flinchAxis = new THREE.Vector3(1, 0, 0);
    this.anim = 'idle';
    this.animTime = 0;
    this.dead = false;
    this.visible = true;
    this.lean = 0;                // -1..1 peek offset

    this.weaponAttach = new THREE.Object3D();
    this.weaponAttach.name = 'proxy-weapon';
    this._bones = {
      head: new THREE.Object3D(), chest: new THREE.Object3D(),
      pelvis: new THREE.Object3D(), muzzle: new THREE.Object3D(),
    };
  }

  /* ---- the characters API ---- */

  setLocomotion(speed, strafe = 0, crouched = false) {
    this.speed = speed;
    this.strafe = strafe;
    this.stanceTarget = crouched ? 0 : 1;
  }
  setAimTarget(p) { if (p) this.aim.copy(p); }
  setLookTarget(p) { if (p) this.look.copy(p); }
  play(name) {
    if (name === this.anim) return;
    this.anim = name;
    this.animTime = 0;
  }
  hit(region, impulse, point) {
    this.flinch = region === 'head' ? 0.9 : 0.55;
    if (impulse && impulse.isVector3) this.flinchAxis.copy(impulse).normalize();
    void point;
  }
  die() { this.dead = true; this.setVisible(false); }
  getBone(name) { return this._bones[name] || null; }
  setVisible(on) {
    this.visible = !!on;
    if (!on) {
      for (let p = 0; p < PART_COUNT; p++) {
        this.pool.meshes[p].setMatrixAt(this.slot, _hidden);
        this.pool.meshes[p].instanceMatrix.needsUpdate = true;
      }
    }
  }
  dispose() { this.pool.release(this); }

  /* ---- posing ---- */

  _pose(dt) {
    if (!this.visible || this.dead) return;
    this.stance += (this.stanceTarget - this.stance) * Math.min(1, dt * 7);
    this.flinch = Math.max(0, this.flinch - dt * 3.2);
    this.animTime += dt;

    const crouch = 1 - this.stance;
    const hipY = 0.92 - crouch * 0.30;

    // stride phase from ground speed
    const stride = Math.min(1, Math.abs(this.speed) / 4.2);
    this.phase += dt * (2.6 + stride * 6.2) * (Math.abs(this.speed) > 0.06 ? 1 : 0);
    const swing = Math.sin(this.phase) * (0.12 + stride * 0.62);
    const bob = Math.abs(Math.sin(this.phase)) * stride * 0.045;

    // aim / look angles, in body space
    _v.copy(this.aim).sub(this.position);
    const aimYaw = Math.atan2(_v.x, _v.z);
    const aimPitch = Math.atan2(_v.y - (hipY + 0.55), Math.hypot(_v.x, _v.z));
    this.aimYaw = aimYaw;
    this.aimPitch = THREE.MathUtils.clamp(aimPitch, -0.9, 0.9);

    _v.copy(this.look).sub(this.position);
    const lookYaw = Math.atan2(_v.x, _v.z);
    this.headYaw = wrapPi(lookYaw - aimYaw) * 0.7;
    this.headPitch = THREE.MathUtils.clamp(
      Math.atan2(_v.y - (hipY + 0.72), Math.hypot(_v.x, _v.z)), -0.7, 0.7) * 0.8;

    const vault = this.anim === 'vault'
      ? Math.sin(Math.min(1, this.animTime / 0.7) * Math.PI) : 0;

    // root transform: feet position, facing the aim yaw, plus the peek lean
    const leanX = Math.cos(aimYaw) * this.lean * 0.34;
    const leanZ = -Math.sin(aimYaw) * this.lean * 0.34;
    const rootY = this.position.y + bob + vault * 0.5;
    const flinchTilt = this.flinch * 0.22;

    const torsoLean = -stride * 0.16 - crouch * 0.14 - flinchTilt;
    _e.set(torsoLean, aimYaw, this.lean * -0.22, 'YXZ');
    _q.setFromEuler(_e);
    const rootM = _m.compose(
      _v.set(this.position.x + leanX, rootY + hipY, this.position.z + leanZ), _q, _one);

    this._set(PART_TORSO, rootM);
    this._bones.pelvis.position.set(this.position.x + leanX, rootY + hipY, this.position.z + leanZ);
    this._bones.chest.position.copy(this._bones.pelvis.position).y += 0.42;

    // head
    _e.set(this.headPitch, this.headYaw, 0, 'YXZ');
    _q.setFromEuler(_e);
    _m2.compose(_v.set(0, 0.60, 0), _q, _one);
    const headM = _m2.premultiply(rootM);
    this._set(PART_HEAD, headM);
    this._bones.head.position.setFromMatrixPosition(headM);
    this._bones.head.position.y += 0.10;

    // arms hold the rifle, so both follow the aim pitch
    const armPitch = this.aimPitch - 1.28;
    _e.set(armPitch, 0.10, -0.34, 'YXZ');
    _q.setFromEuler(_e);
    _m2.compose(_v.set(-0.20, 0.53, 0.03), _q, _one);
    this._set(PART_ARM_L, _tmpMul(rootM, _m2));

    _e.set(armPitch + 0.20, -0.06, 0.30, 'YXZ');
    _q.setFromEuler(_e);
    _m2.compose(_v.set(0.20, 0.53, -0.02), _q, _one);
    this._set(PART_ARM_R, _tmpMul(rootM, _m2));

    // legs: alternating swing, folded when crouched
    _e.set(swing - crouch * 0.75, 0, 0.02, 'YXZ');
    _q.setFromEuler(_e);
    _m2.compose(_v.set(-0.095, 0.02, 0), _q, _one);
    this._set(PART_LEG_L, _tmpMul(rootM, _m2));

    _e.set(-swing - crouch * 0.75, 0, -0.02, 'YXZ');
    _q.setFromEuler(_e);
    _m2.compose(_v.set(0.095, 0.02, 0), _q, _one);
    this._set(PART_LEG_R, _tmpMul(rootM, _m2));

    // rifle, held at the shoulder along the aim direction
    _e.set(this.aimPitch, 0, 0, 'YXZ');
    _q.setFromEuler(_e);
    _m2.compose(_v.set(0.10, 0.50, 0.14), _q, _one);
    const rifleM = _tmpMul(rootM, _m2);
    this._set(PART_RIFLE, rifleM);
    this.weaponAttach.position.setFromMatrixPosition(rifleM);
    this.weaponAttach.quaternion.setFromRotationMatrix(rifleM);
    this._bones.muzzle.position.set(0, 0.012, 0.72).applyMatrix4(rifleM);
  }

  _set(part, matrix) {
    this.pool.meshes[part].setMatrixAt(this.slot, matrix);
  }

  /** Muzzle position in world space. */
  muzzle(out) { return out.copy(this._bones.muzzle.position); }
  /** Eye position in world space. */
  eye(out) { return out.copy(this._bones.head.position); }
}

const _mulA = new THREE.Matrix4();
function _tmpMul(a, b) { return _mulA.copy(a).multiply(b); }

function wrapPi(a) {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

export function createProxyPool(ctx, capacity) {
  return new ProxyPool(ctx, capacity);
}
